import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Mic2, MoveHorizontal, Sparkles, Wand2 } from "lucide-react";

const copyByLanguage = {
  es: {
    badge: "Guía rápida",
    title: "Cómo funciona la edición de speakers",
    description: "Aquí ves cómo crear un speaker nuevo, mover rangos y ajustar el timeline sin salir de la transcripción.",
    close: "Volver a la transcripción",
    sections: [
      {
        title: "1. Empieza con IA",
        body: "Si todavía no hay speakers, pulsa `Identificar speakers`. Voxara revisa la transcripción y propone hablantes para arrancar mucho más rápido.",
        icon: Sparkles,
      },
      {
        title: "2. Crear un speaker nuevo",
        body: "Dentro de la barra del timeline, haz click, deja oprimido y arrastra para marcar un tramo. Luego escribe el nombre en el campo y pulsa `Marcar speaker` para crear ese nuevo hablante en ese rango.",
        icon: Wand2,
      },
      {
        title: "3. Mover o ajustar un rango",
        body: "Haz click sobre un bloque o sobre el speaker y activa `Editar rango`. Usa el control central para mover todo el bloque o los laterales para alargar o recortar el inicio y el final.",
        icon: MoveHorizontal,
      },
      {
        title: "4. Revisar antes de exportar",
        body: "Puedes renombrar speakers, saltar a un tramo concreto o borrar uno desde el chip del speaker. Lo ideal es revisar aquí antes de exportar a PDF o Word.",
        icon: Mic2,
      },
    ],
  },
  en: {
    badge: "Quick guide",
    title: "How speaker editing works",
    description: "Here is how to create a new speaker, move ranges, and adjust the timeline without leaving the transcript.",
    close: "Back to transcript",
    sections: [
      {
        title: "1. Start with AI",
        body: "If there are no speakers yet, click `Identify speakers`. Voxara reviews the transcript and proposes speakers so you can start much faster.",
        icon: Sparkles,
      },
      {
        title: "2. Create a new speaker",
        body: "Inside the timeline bar, click, hold, and drag to mark a span. Then type the name in the input and click `Mark speaker` to create that new speaker for that range.",
        icon: Wand2,
      },
      {
        title: "3. Move or adjust a range",
        body: "Click a block or a speaker, then enable `Edit range`. Use the center handle to move the whole block or the side handles to extend or trim the start and end.",
        icon: MoveHorizontal,
      },
      {
        title: "4. Review before export",
        body: "You can rename speakers, jump to a specific span, or delete one from the speaker chip. The best workflow is to review here before exporting to PDF or Word.",
        icon: Mic2,
      },
    ],
  },
};

export const SpeakerTimelineHelpDialog = ({ language = "es", open, onOpenChange }) => {
  const copy = copyByLanguage[language] || copyByLanguage.es;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl overflow-hidden border-primary/20 bg-[#090910]/95 p-0 text-white shadow-[0_30px_90px_rgba(0,0,0,0.55)]"
        closeButtonTestId="speaker-timeline-help-close-icon-button"
        data-testid="speaker-timeline-help-dialog"
      >
        <div className="relative overflow-hidden px-6 py-6 sm:px-8 sm:py-8">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(248,0,248,0.22),transparent_42%),radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.08),transparent_36%)]" />

          <div className="relative">
            <DialogHeader className="space-y-4 text-left">
              <Badge className="w-fit rounded-full border border-primary/25 bg-primary/12 px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-primary" data-testid="speaker-timeline-help-badge">
                {copy.badge}
              </Badge>
              <DialogTitle className="max-w-[18ch] text-3xl font-bold leading-[1.02] tracking-tight text-white sm:text-4xl" data-testid="speaker-timeline-help-title">
                {copy.title}
              </DialogTitle>
              <DialogDescription className="max-w-2xl text-sm leading-7 text-white/72 sm:text-base" data-testid="speaker-timeline-help-description">
                {copy.description}
              </DialogDescription>
            </DialogHeader>

            <div className="mt-6 grid gap-3" data-testid="speaker-timeline-help-sections">
              {copy.sections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <div
                    key={section.title}
                    className="flex items-start gap-3 rounded-2xl border border-white/8 bg-white/5 px-4 py-3 text-left"
                    data-testid={`speaker-timeline-help-section-${index + 1}`}
                  >
                    <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary shadow-[0_0_18px_rgba(248,0,248,0.16)]">
                      <Icon className="h-4.5 w-4.5" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white sm:text-[0.98rem]" data-testid={`speaker-timeline-help-section-title-${index + 1}`}>
                        {section.title}
                      </h3>
                      <p className="mt-1 text-sm leading-6 text-white/78" data-testid={`speaker-timeline-help-section-body-${index + 1}`}>
                        {section.body}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <DialogFooter className="mt-7">
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                className="speaker-edit-cta h-12 rounded-2xl px-6 text-sm font-semibold"
                data-testid="speaker-timeline-help-close-button"
              >
                {copy.close}
              </Button>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SpeakerTimelineHelpDialog;